import type { ComponentPublicInstance, } from "vue"

type ElementRef = Ref<HTMLElement | ComponentPublicInstance | undefined>
interface UseMaxHeightOptions {
  targetRef: ElementRef
  otherRefs?: ElementRef[] | ElementRef
  minHeight?: number
  offset?: number | Ref<number>
}

export function useMaxHeight({
  targetRef,
  otherRefs,
  minHeight = 300,
  // ContentWrap的底部padding 16 + AppContent padding 20
  offset = 36,
}: UseMaxHeightOptions,) {
  const maxHeight = ref(minHeight,)

  const otherEls = computed(() => {
    if (!otherRefs) {
      return []
    }
    const refs = Array.isArray(otherRefs,) ? otherRefs : [otherRefs,]
    return refs.map(ref => unref(ref,),)
  },)

  // 获取目标元素距离顶部的距离，宽度和高度
  const { top: targetTop, height: targetHeight, } = useElementBounding(targetRef,)

  const calculateMaxHeight = useDebounceFn(() => {
    if (!unref(targetRef,)) {
      return
    }

    // 获取视口高度
    const viewportHeight = window.innerHeight

    // 计算其他元素的总高度
    const otherHeight = unref(otherEls,).reduce((total, ref,) => {
      const { height, } = useElementSize(ref,)
      total += unref(height,)
      return total
    }, 0,)

    // 计算剩余空间
    const availableHeight = viewportHeight - unref(targetTop,) - otherHeight - unref(offset,)

    // 当前元素的最大高度 = 剩余空间 或 最小高度（二者取较大值）
    maxHeight.value = Math.round(Math.max(availableHeight, minHeight,),)
  }, 300,)

  // 初始化和窗口大小变化时重新计算
  onMounted(() => {
    window.addEventListener("resize", calculateMaxHeight,)
  },)

  // 清理事件监听器
  onBeforeUnmount(() => {
    window.removeEventListener("resize", calculateMaxHeight,)
  },)

  watch(
    () => [
      unref(targetRef,),
      unref(offset,),
      unref(targetTop,),
      unref(targetHeight,),
      ...unref(otherEls,),
    ],
    calculateMaxHeight,
  )

  return {
    maxHeight,
    calculateMaxHeight,
  }
}
